import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Empty,
  Grid,
  List,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ClockCircleOutlined,
  CrownOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useAuth } from "../context/AuthContext";
import useAuditScope from "./useAuditScope";
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
} from "./auditRequestGate";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// IMPORTANT (CPU) :
//  - Aucune connexion temps reel (websocket) : on lit users.last_activity
//    a la demande. Auto-refresh par defaut DESACTIVE.
//  - Quand active, intervalle minimum = 60s (un seul SELECT, indexe).
const AUTO_REFRESH_INTERVALS = [
  { value: 60000, label: "1 min" },
  { value: 120000, label: "2 min" },
  { value: 300000, label: "5 min" },
];

const WINDOW_OPTIONS = [
  { value: 5, label: "5 min" },
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "60 min" },
];

const formatIdle = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "-";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMin = minutes % 60;
  return remainingMin ? `${hours}h ${remainingMin}min` : `${hours}h`;
};

const renderIdleTag = (seconds) => {
  if (!Number.isFinite(seconds)) return <Tag>Inconnu</Tag>;
  if (seconds < 60) {
    return <Tag color="green">En ligne</Tag>;
  }
  if (seconds < 5 * 60) {
    return <Tag color="lime">Actif il y a {formatIdle(seconds)}</Tag>;
  }
  return <Tag color="orange">Inactif depuis {formatIdle(seconds)}</Tag>;
};

const AuditSessions = () => {
  const { token } = useAuth();
  const {
    auditScope,
    companyId,
    isSuperAdmin,
    scope: effectiveAuditScope,
    scopeKey: auditScopeKey,
    setAuditScope,
  } = useAuditScope();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [windowMinutes, setWindowMinutes] = useState(15);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(120000); // 2 min par defaut
  const intervalRef = useRef(null);
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => {
    latestAuditScopeKeyRef.current = auditScopeKey;
  }, [auditScopeKey]);
  const previousAuditScopeKeyRef = useRef(auditScopeKey);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) {
    requestGateRef.current = createAuditRequestGate();
  }

  const loadSessions = useCallback(
    async (silent = false) => {
      const request = requestGateRef.current.start(auditScopeKey);
      if (!silent) setLoading(true);
      try {
        const params = new URLSearchParams({
          windowMinutes: String(windowMinutes),
        });
        if (effectiveAuditScope === "all") {
          params.append("scope", "all");
        }
        const response = await axios.get(
          `/api/audit/auth/active?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(companyId ? { "X-Company-Id": String(companyId) } : {}),
            },
            signal: request.signal,
          }
        );

        if (
          request.isCurrent(latestAuditScopeKeyRef.current) &&
          response.data?.success
        ) {
          setSessions(response.data.sessions || []);
          setGeneratedAt(response.data.generated_at || new Date().toISOString());
        }
      } catch (error) {
        if (
          isAuditRequestCanceled(error, request.signal) ||
          !request.isCurrent(latestAuditScopeKeyRef.current)
        ) {
          return;
        }
        console.error("[AUDIT_SESSIONS] Erreur chargement:", error);
        if (!silent) {
          message.error("Impossible de charger les sessions actives");
        }
      } finally {
        const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
        request.finish();
        if (isCurrent) setLoading(false);
      }
    },
    [auditScopeKey, companyId, effectiveAuditScope, token, windowMinutes]
  );

  useEffect(() => {
    const scopeChanged = previousAuditScopeKeyRef.current !== auditScopeKey;
    previousAuditScopeKeyRef.current = auditScopeKey;
    if (scopeChanged) {
      requestGateRef.current.cancel();
      setSessions([]);
      setGeneratedAt(null);
    }
    loadSessions();
  }, [auditScopeKey, loadSessions]);

  useEffect(
    () => () => {
      requestGateRef.current?.cancel();
    },
    []
  );

  // Auto-refresh : intervalle unique nettoye a chaque toggle/changement
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        loadSessions(true);
      }, Math.max(60000, refreshInterval));
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, refreshInterval, loadSessions]);

  const totals = useMemo(() => {
    const onlineNow = sessions.filter((s) => s.idle_seconds < 60).length;
    const superadmins = sessions.filter(
      (s) => s.is_superadmin === 1 || s.is_superadmin === true
    ).length;
    return {
      total: sessions.length,
      onlineNow,
      superadmins,
    };
  }, [sessions]);

  const columns = useMemo(
    () => [
      {
        title: "Utilisateur",
        key: "user",
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              {record.is_superadmin === 1 || record.is_superadmin === true ? (
                <Tooltip title="SuperAdmin">
                  <CrownOutlined style={{ color: "#faad14" }} />
                </Tooltip>
              ) : (
                <UserOutlined />
              )}
              <Text strong>{record.username || "-"}</Text>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Text>
          </Space>
        ),
      },
      {
        title: "Entreprise",
        dataIndex: "company_name",
        key: "company_name",
        responsive: ["md"],
        render: (value) => value || <Text type="secondary">-</Text>,
      },
      {
        title: "Statut",
        key: "status",
        width: 200,
        render: (_, record) => renderIdleTag(record.idle_seconds),
      },
      {
        title: "Derniere activite",
        dataIndex: "last_activity",
        key: "last_activity",
        responsive: ["md"],
        render: (value) =>
          value ? (
            <Tooltip title={dayjs(value).format("DD/MM/YYYY HH:mm:ss")}>
              <Text>{dayjs(value).format("DD/MM HH:mm:ss")}</Text>
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        title: "Connexion",
        dataIndex: "login_at",
        key: "login_at",
        responsive: ["lg"],
        render: (value) =>
          value ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(value).format("DD/MM HH:mm")}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: "IP",
        dataIndex: "login_ip",
        key: "login_ip",
        responsive: ["lg"],
        render: (value) =>
          value ? (
            <Text code style={{ fontSize: 12 }}>
              {value}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
    ],
    []
  );

  const renderMobileItem = (record) => (
    <List.Item key={record.user_id}>
      <Card size="small" style={{ width: "100%" }}>
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Space>
            {record.is_superadmin === 1 || record.is_superadmin === true ? (
              <CrownOutlined style={{ color: "#faad14" }} />
            ) : (
              <UserOutlined />
            )}
            <Text strong>{record.username || record.email}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.email}
          </Text>
          {record.company_name ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.company_name}
            </Text>
          ) : null}
          {renderIdleTag(record.idle_seconds)}
          {record.last_activity ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Vu : {dayjs(record.last_activity).format("DD/MM HH:mm:ss")}
            </Text>
          ) : null}
          {record.login_ip ? (
            <Text code style={{ fontSize: 11 }}>
              {record.login_ip}
            </Text>
          ) : null}
        </Space>
      </Card>
    </List.Item>
  );

  return (
    <div className="audit-sessions" style={{ padding: isMobile ? 8 : 16 }}>
      <Card
        title={
          <Space>
            <TeamOutlined />
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              Sessions actives
            </Title>
            <Badge
              count={totals.total}
              style={{ backgroundColor: "#1890ff" }}
              showZero
            />
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadSessions(false)}
            loading={loading}
            size={isMobile ? "small" : "middle"}
          >
            {isMobile ? "" : "Actualiser"}
          </Button>
        }
        bordered={false}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Lecture à la demande sur <Text code>users.last_activity</Text>. Pas
              de WebSocket, pas de polling serveur — impact CPU négligeable.
              L'auto-actualisation est désactivée par défaut.
            </span>
          }
        />

        <Space wrap size="middle" style={{ marginBottom: 16 }}>
          <Statistic
            title="Sessions actives"
            value={totals.total}
            prefix={<TeamOutlined />}
          />
          <Statistic
            title="En ligne (< 1min)"
            value={totals.onlineNow}
            valueStyle={{ color: "#52c41a" }}
          />
          <Statistic
            title="Dont SuperAdmins"
            value={totals.superadmins}
            prefix={<CrownOutlined />}
            valueStyle={{ color: "#faad14" }}
          />
        </Space>

        <Space wrap size="middle" style={{ marginBottom: 16 }}>
          {isSuperAdmin ? (
            <Space>
              <Text>Périmètre :</Text>
              <Select
                value={auditScope}
                onChange={(value) => setAuditScope(value || "company")}
                style={{ width: 210 }}
              >
                <Option value="company">Entreprise sélectionnée</Option>
                <Option value="all">Toutes les entreprises</Option>
              </Select>
            </Space>
          ) : null}
          <Space>
            <Text>Fenêtre :</Text>
            <Select
              value={windowMinutes}
              onChange={(value) => setWindowMinutes(value)}
              style={{ width: 110 }}
            >
              {WINDOW_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Space>

          <Space>
            <Text>Auto-actualisation :</Text>
            <Switch checked={autoRefresh} onChange={setAutoRefresh} />
            {autoRefresh ? (
              <Select
                value={refreshInterval}
                onChange={setRefreshInterval}
                style={{ width: 110 }}
              >
                {AUTO_REFRESH_INTERVALS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            ) : null}
          </Space>

          {generatedAt ? (
            <Space>
              <ClockCircleOutlined />
              <Text type="secondary">
                Mis à jour à {dayjs(generatedAt).format("HH:mm:ss")}
              </Text>
            </Space>
          ) : null}
        </Space>

        {sessions.length === 0 && !loading ? (
          <Empty description="Aucune session active dans cette fenêtre" />
        ) : isMobile ? (
          <List
            loading={loading}
            dataSource={sessions}
            renderItem={renderMobileItem}
          />
        ) : (
          <Table
            loading={loading}
            dataSource={sessions}
            columns={columns}
            rowKey="user_id"
            pagination={false}
            size="middle"
          />
        )}
      </Card>
    </div>
  );
};

export default AuditSessions;
